/**
 * <s-sticky-buy-bar> — Fixed "Add to bag" bar on the product page.
 *
 * Shows (data-visible, not inert) once the main <s-product-form> has scrolled
 * above the viewport, hides when it is back in view or the page is above it.
 * The button clicks the main form's add button so cart behaviour stays in one
 * place; when that button is unavailable it scrolls the form into view.
 * Price and button label are mirrored from the main page after variant changes.
 *
 * Child markup: [data-sticky-add], [data-bar-price], [data-bar-compare].
 */
class SStickyBuyBar extends HTMLElement {
  #ac;
  #observer;
  #mutations;

  connectedCallback() {
    this.#ac = new AbortController();
    const form = document.querySelector('s-product-form');
    if (!form) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      // Visible only after the form has scrolled up past the top of the viewport.
      const past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.#setVisible(past);
    });
    this.#observer.observe(form);

    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => this.#add(form), { signal: this.#ac.signal });

    // The product-title and product-form partials are swapped on variant change; mirror price and label.
    this.#mutations = new MutationObserver(() => this.#mirror(form));
    const scope = form.parentElement || form;
    this.#mutations.observe(scope, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
  }

  #setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
  }

  #mainButton(form) {
    const button = form.querySelector('button[name="add"]');
    return button instanceof HTMLButtonElement && button.getAttribute('aria-disabled') !== 'true' && !button.disabled
      ? button
      : null;
  }

  #add(form) {
    const button = this.#mainButton(form);
    if (button) {
      button.click();
      return;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    form.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  }

  #mirror(form) {
    const scope = form.parentElement || form;
    const main = scope.querySelector('[data-product-price]');
    const price = this.querySelector('[data-bar-price]');
    const compare = this.querySelector('[data-bar-compare]');
    if (main && price) {
      const sale = main.querySelector('.text-sale bdi');
      const regular = main.querySelector('bdi');
      const was = main.querySelector('s bdi');
      const text = (sale || regular)?.textContent?.trim();
      if (text && price.textContent !== text) price.textContent = text;
      price.classList.toggle('text-sale', Boolean(sale && was));
      price.classList.toggle('text-on-surface', !(sale && was));
      if (compare instanceof HTMLElement) {
        compare.hidden = !was;
        const wasText = was?.textContent?.trim() || '';
        if (compare.textContent !== wasText) compare.textContent = wasText;
      }
    }
    const label = this.querySelector('[data-sticky-add]');
    const mainButton = form.querySelector('button[name="add"], button[inert]');
    const labelText = mainButton?.textContent?.trim();
    if (label && labelText && label.textContent !== labelText) label.textContent = labelText;
  }
}

if (!customElements.get('s-sticky-buy-bar')) {
  customElements.define('s-sticky-buy-bar', SStickyBuyBar);
}
